/**
 * Payment Terminal Adapter — Verifone / PAX / Ingenico / BKM TechPOS.
 *
 * Vendor SDK çağrısı + backend transaction lifecycle:
 * 1. Backend POST /terminal-transactions/ → INITIATED state
 * 2. Frontend vendor SDK ile cihazı tetikler (PaxPosLink, Verifone iSC vs.)
 * 3. Vendor callback'i → PATCH /terminal-transactions/{id}/report-result/
 *    ile sonucu backend'e bildir
 * 4. Backend transaction.status = APPROVED → Payment kaydı yaratılır
 */

import { apiClient } from "../api";
import { isPaxPaymentAvailable, startPaxIntent } from "./paxPaymentPlugin";

export interface TerminalChargeOptions {
  device_id: string;
  order_id: string;
  amount_minor: number;
  installments?: number;
}

export interface TerminalResult {
  status: "APPROVED" | "DECLINED" | "CANCELLED" | "TIMEOUT" | "ERROR";
  terminal_transaction_id?: string;
  auth_code?: string;
  card_last_4?: string;
  card_type?: string;
  installment_count?: number;
  error_message?: string;
  raw?: any;
}

/**
 * Backend'in PAX Intent cihazları için döndürdüğü Intent payload.
 * `POST /terminal-transactions/` response'undaki `intent_payload` alanı.
 * Capacitor app bunu doğrudan startActivityForResult Intent'ine çevirir.
 * Bkz. backend docs/PAX_A920_INTENT_INTEGRATION.md.
 */
export interface IntentPayload {
  action: string;
  extras: {
    transType: string;
    amount: number; // kuruş
    currency: string;
    orderId: string;
    installment: number;
  };
}

interface TerminalAdapter {
  vendor: string;
  isAvailable(): Promise<boolean>;
  /**
   * Cihazı tetikler. `intentPayload` backend'den geldiyse (PAX Intent
   * senaryosu) onun parametreleri kullanılır; gelmezse adapter kendi
   * parametrelerini kurar (geriye uyumlu — eski SDK akışları).
   */
  charge(
    amount_minor: number,
    installments: number,
    intentPayload?: IntentPayload,
  ): Promise<TerminalResult>;
}

class VerifoneAdapter implements TerminalAdapter {
  vendor = "VERIFONE";
  async isAvailable(): Promise<boolean> {
    const w = window as any;
    return !!w.VerifonePaymentSDK || !!w.Capacitor?.Plugins?.VerifonePayment;
  }
  async charge(amount_minor: number, installments: number): Promise<TerminalResult> {
    const w = window as any;
    const sdk = w.VerifonePaymentSDK || w.Capacitor.Plugins.VerifonePayment;
    const res = await sdk.startTransaction({
      amount: amount_minor / 100,
      currency: "TRY",
      installments,
    });
    return {
      status: res.approved ? "APPROVED" : "DECLINED",
      terminal_transaction_id: res.transactionId,
      auth_code: res.authCode,
      card_last_4: res.cardLast4,
      card_type: res.cardType,
      installment_count: res.installments || 1,
      raw: res,
    };
  }
}

class PaxAdapter implements TerminalAdapter {
  vendor = "PAX";
  async isAvailable(): Promise<boolean> {
    // Tipli Capacitor PaxPayment plugin'i (Intent köprüsü) veya eski
    // window.PaxPosLink (SDK) — ikisinden biri yeterli.
    const w = window as any;
    return isPaxPaymentAvailable() || !!w.PaxPosLink;
  }
  async charge(
    amount_minor: number,
    installments: number,
    intentPayload?: IntentPayload,
  ): Promise<TerminalResult> {
    // PAX Intent senaryosu: backend'in verdiği intent_payload doğrudan
    // banka ödeme app'ine Intent olarak iletilir (action + extras).
    let res: { resultCode: string; transactionNumber?: string;
               approvalCode?: string; cardNumber?: string;
               cardType?: string; message?: string };

    if (intentPayload && isPaxPaymentAvailable()) {
      // Tercih edilen yol: native Capacitor plugin'i (PaxPaymentPlugin.kt).
      // @capacitor/core startPaxIntent içinde lazy import edilir.
      res = await startPaxIntent(intentPayload);
    } else {
      // Geriye uyum: eski window.PaxPosLink SDK'sı (Intent dışı senaryolar).
      const sdk = (window as any).PaxPosLink;
      if (!sdk?.doCredit) {
        throw new Error("PAX ödeme köprüsü mevcut değil (Capacitor app içinde miyiz?)");
      }
      res = await sdk.doCredit({
        transType: intentPayload?.extras.transType ?? "SALE",
        amount: intentPayload?.extras.amount ?? amount_minor, // PAX kuruş kabul eder
        installmentCount: intentPayload?.extras.installment ?? installments,
      });
    }

    const approved = res.resultCode === "000000";
    const cancelled = res.resultCode === "CANCELLED";
    return {
      status: approved ? "APPROVED" : cancelled ? "CANCELLED" : "DECLINED",
      card_last_4: (res.cardNumber || "").slice(-4),
      installment_count: installments,
      error_message: approved ? "" : res.message || `Code ${res.resultCode}`,
      raw: res,
      ...(res.transactionNumber !== undefined
        ? { terminal_transaction_id: res.transactionNumber }
        : {}),
      ...(res.approvalCode !== undefined ? { auth_code: res.approvalCode } : {}),
      ...(res.cardType !== undefined ? { card_type: res.cardType } : {}),
    };
  }
}

class IngenicoAdapter implements TerminalAdapter {
  vendor = "INGENICO";
  async isAvailable(): Promise<boolean> {
    const w = window as any;
    return !!w.IngenicoSDK || !!w.Capacitor?.Plugins?.IngenicoPayment;
  }
  async charge(amount_minor: number, installments: number): Promise<TerminalResult> {
    const w = window as any;
    const sdk = w.IngenicoSDK || w.Capacitor.Plugins.IngenicoPayment;
    const res = await sdk.purchase({
      amount: amount_minor,
      currency: "TRY",
      installments,
    });
    return {
      status: res.status === "OK" ? "APPROVED" : "DECLINED",
      terminal_transaction_id: res.refNo,
      auth_code: res.authCode,
      card_last_4: res.maskedPan?.slice(-4),
      installment_count: installments,
      raw: res,
    };
  }
}

class SunmiPaymentAdapter implements TerminalAdapter {
  vendor = "SUNMI";
  async isAvailable(): Promise<boolean> {
    const w = window as any;
    return !!w.SunmiPayment || !!w.Capacitor?.Plugins?.SunmiPayment;
  }
  async charge(amount_minor: number, installments: number): Promise<TerminalResult> {
    const w = window as any;
    const sdk = w.SunmiPayment || w.Capacitor.Plugins.SunmiPayment;
    const res = await sdk.startTransaction({ amount: amount_minor / 100, currency: "TRY" });
    return {
      status: res.success ? "APPROVED" : "DECLINED",
      terminal_transaction_id: res.transactionId,
      auth_code: res.authCode,
      card_last_4: res.cardLast4,
      installment_count: installments,
      raw: res,
    };
  }
}

const ADAPTERS: TerminalAdapter[] = [
  new VerifoneAdapter(),
  new PaxAdapter(),
  new IngenicoAdapter(),
  new SunmiPaymentAdapter(),
];

async function resolveAdapter(): Promise<TerminalAdapter | null> {
  for (const a of ADAPTERS) {
    if (await a.isAvailable()) return a;
  }
  return null;
}

export const paymentTerminalAdapter = {
  /**
   * Tam çekim akışı: backend transaction yarat → vendor SDK çağır →
   * sonucu backend'e raporla.
   */
  async charge(opts: TerminalChargeOptions): Promise<{ transaction_id: string; result: TerminalResult }> {
    const adapter = await resolveAdapter();
    if (!adapter) {
      throw new Error("Hiçbir POS terminal SDK'sı mevcut değil. Capacitor app içinde miyiz?");
    }

    // 1. Backend transaction oluştur. PAX Intent cihazlarında response
    //    `intent_payload` taşır — adapter bunu doğrudan kullanır.
    const { data: tx } = await apiClient.post("/api/v1/terminal-transactions/", {
      device: opts.device_id,
      order: opts.order_id,
      amount_minor: opts.amount_minor,
      installment_count: opts.installments || 1,
      status: "INITIATED",
    });
    const intentPayload: IntentPayload | undefined = tx.intent_payload;

    // 2. Vendor SDK çağır — backend'in intent_payload'u varsa onunla.
    let result: TerminalResult;
    try {
      result = await adapter.charge(
        opts.amount_minor,
        opts.installments || 1,
        intentPayload,
      );
    } catch (e: any) {
      result = {
        status: "ERROR",
        error_message: e?.message || String(e),
      };
    }

    // 3. Sonucu backend'e raporla
    await apiClient.patch(`/api/v1/terminal-transactions/${tx.id}/report-result/`, {
      status: result.status,
      terminal_transaction_id: result.terminal_transaction_id || "",
      auth_code: result.auth_code || "",
      card_last_4: result.card_last_4 || "",
      card_type: result.card_type || "",
      installment_count: result.installment_count || 1,
      raw_response: result.raw || {},
      error_message: result.error_message || "",
    });

    return { transaction_id: tx.id, result };
  },

  async resolveAdapter() {
    return resolveAdapter();
  },
};
